import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { ApprovalsDashboardPage } from "@/features/approvals/pages/approvals-dashboard";
import { AdminLoginPage } from "@/features/auth/pages/admin-login";
import { AdminVerifyOtpPage } from "@/features/auth/pages/admin-verify-otp";
import { BoqTemplateEditorPage } from "@/features/boq-templates/pages/template-editor";
import { BoqTemplatesDashboardPage } from "@/features/boq-templates/pages/templates-dashboard";
import { BrandEditorPage } from "@/features/catalog/pages/brand-editor";
import { BrandsDashboardPage } from "@/features/catalog/pages/brands-dashboard";
import { CatalogDashboardPage } from "@/features/catalog/pages/catalog-dashboard";
import { CatalogCategoriesPage } from "@/features/catalog/pages/categories-dashboard";
import { CatalogGstRatePrefixPage } from "@/features/catalog/pages/gst-rate-prefix";
import { CatalogGstRatesPage } from "@/features/catalog/pages/gst-rates-dashboard";
import { CatalogImportRunPage } from "@/features/catalog/pages/import-run";
import { CatalogImportsPage } from "@/features/catalog/pages/imports-dashboard";
import { CatalogProductCreatePage } from "@/features/catalog/pages/product-create";
import { CatalogProductEditorPage } from "@/features/catalog/pages/product-editor";
import { CatalogProductsPage } from "@/features/catalog/pages/products-dashboard";
import { CatalogSourcesPage } from "@/features/catalog/pages/sources-dashboard";
import { SpecTemplateEditorPage } from "@/features/catalog/pages/spec-template-editor";

/*
 * Remounts the editor when the category in the URL changes.
 *
 * EXPORTED for its test. React Router reuses one element across param changes,
 * and the editor seeds its draft ONCE -- so without the key, navigating from
 * one category's template to another leaves the first's attributes under the
 * second's heading, ready to be saved onto it. Rendering the page directly, as
 * the editor's own suite does, cannot see that: the wrapper is the whole fix,
 * and until this was exported nothing rendered the wrapper at all.
 */
export function KeyedSpecTemplateEditor() {
	const { categoryId = "" } = useParams();
	return <SpecTemplateEditorPage key={categoryId} />;
}

import { GST_READ_PERMISSIONS } from "@/features/catalog/lib/gst-rates";
import { AdminShell } from "@/features/layout/admin-shell";
import { ProtectedAdminRoute } from "@/features/layout/protected-admin-route";
import { RequirePermission } from "@/features/layout/require-permission";
import { LogsDashboardPage } from "@/features/logs/pages/logs-dashboard";
import { PaymentsDashboardPage } from "@/features/payments/pages/payments-dashboard";
import { RolesDashboardPage } from "@/features/roles/pages/roles-dashboard";
import { UsersDashboardPage } from "@/features/users/pages/users-dashboard";
import { VendorsDashboardPage } from "@/features/vendors/pages/vendors-dashboard";

export const router = createBrowserRouter([
	{
		path: "/login",
		element: <AdminLoginPage />,
	},
	{
		path: "/verify-otp",
		element: <AdminVerifyOtpPage />,
	},
	{
		path: "/",
		element: (
			<ProtectedAdminRoute>
				<AdminShell />
			</ProtectedAdminRoute>
		),
		children: [
			{
				index: true,
				element: <Navigate to="/payments" replace />,
			},
			{
				path: "approvals",
				element: <ApprovalsDashboardPage />,
			},
			{
				/*
				 * The catalogue's landing screen, at `/catalog` rather than under a
				 * `dashboard` segment: the nav's "Catalogue" entry points here, and an
				 * operator who types the section name should arrive at its front page
				 * rather than at a 404.
				 *
				 * Same gate as the lists it summarises, and for the same reason --
				 * ungated it renders and every request 403s behind a generic failure,
				 * which is indistinguishable from the server being down.
				 */
				path: "catalog",
				element: (
					<RequirePermission permission="admin:catalog:read">
						<CatalogDashboardPage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/products",
				/*
				 * Gated, so an operator without `admin:catalog:read` is told what is
				 * wrong. Ungated, the page rendered and every request 403'd, and the
				 * screen's generic "Could not load products. Try again" offered a
				 * retry that can never succeed -- indistinguishable from the server
				 * being down.
				 *
				 * This is presentation, not enforcement: the server refuses either
				 * way. The nav item is already hidden by `canSeeNav`; this covers the
				 * operator who arrives by URL or bookmark.
				 */
				element: (
					<RequirePermission permission="admin:catalog:read">
						<CatalogProductsPage />
					</RequirePermission>
				),
			},
			{
				/*
				 * BEFORE the `:productId` route, though React Router does not need it
				 * to be: it ranks a static segment above a dynamic one regardless of
				 * order. Written in this order anyway because the consequence of ever
				 * losing that ranking is not a 404 -- `getProductSchema` validates the
				 * id as a uuid, so "new" would reach the editor and come back
				 * "Invalid product ID" on a screen the operator opened to create
				 * something.
				 */
				path: "catalog/products/new",
				element: (
					<RequirePermission permission="admin:catalog:write">
						<CatalogProductCreatePage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/products/:productId",
				element: <CatalogProductEditorPage />,
			},
			{
				path: "catalog/categories",
				/*
				 * Gated on READ, not on taxonomy.
				 *
				 * Seeing the shape of the catalogue is ordinary catalogue work -- an
				 * operator filing products needs to know what "Wall Tiles" sits under
				 * even though they may not rename it. The screen hides its own edit
				 * controls behind `admin:catalog:taxonomy`, and the server refuses
				 * either way.
				 */
				element: (
					<RequirePermission permission="admin:catalog:read">
						<CatalogCategoriesPage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/categories/:categoryId/template",
				/*
				 * Gated on TAXONOMY, unlike the tree beside it.
				 *
				 * The tree is readable by anyone with catalogue read, because knowing
				 * where a product is filed is ordinary work. A template is only
				 * interesting to whoever may change it -- the product editor already
				 * receives the whole template through `getProduct` -- and every template
				 * endpoint this screen depends on is priced at taxonomy, so
				 * a lower gate here would render a heading and nothing under it. (Not
				 * "every request": the category list it reads for the name is priced at
				 * read, and would still answer.)
				 */
				/*
				 * KEYED ON THE CATEGORY, because the editor seeds its draft once and
				 * React Router reuses an element across param changes. Without this,
				 * navigating from one category's template to another's -- by URL, or
				 * by any future cross-link -- would leave the first category's
				 * attributes under the second's heading, ready to be saved onto it.
				 * The same seed-once shape produced a P1 in #305 -- cited by number
				 * because "two PRs ago" was already wrong by one when it was written,
				 * and drifts further with every merge.
				 */
				element: (
					<RequirePermission permission="admin:catalog:taxonomy">
						<KeyedSpecTemplateEditor />
					</RequirePermission>
				),
			},
			{
				path: "catalog/brands",
				// Gated like the product list, and for the same reason: ungated, the
				// page renders and every request 403s behind a generic "could not
				// load", offering a retry that can never succeed.
				element: (
					<RequirePermission permission="admin:catalog:read">
						<BrandsDashboardPage />
					</RequirePermission>
				),
			},
			{
				/*
				 * BEFORE `:brandId`, matching the product routes. React Router ranks a
				 * static segment above a dynamic one regardless of order, so this is
				 * written for the reader rather than the router -- and because if that
				 * ranking were ever lost the failure is not a 404: the id is validated
				 * as a uuid, so "new" would reach the editor and come back "Invalid
				 * brand ID" on a screen opened to create something.
				 */
				path: "catalog/brands/new",
				element: (
					<RequirePermission permission="admin:catalog:brands">
						<BrandEditorPage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/brands/:brandId",
				element: (
					<RequirePermission permission="admin:catalog:read">
						<BrandEditorPage />
					</RequirePermission>
				),
			},
			{
				/*
				 * Gated on `admin:catalog:import` rather than `read`, matching the
				 * routes behind it. Ungated the page renders and every request 403s
				 * behind a generic "could not load", offering a retry that can never
				 * succeed -- the same reasoning as the product and brand lists.
				 */
				path: "catalog/imports",
				element: (
					<RequirePermission permission="admin:catalog:import">
						<CatalogImportsPage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/imports/:runId",
				element: (
					<RequirePermission permission="admin:catalog:import">
						<CatalogImportRunPage />
					</RequirePermission>
				),
			},
			{
				/*
				 * Gated for the same reason as the two above: ungated, the page
				 * renders, `GET /sources` 403s, and the operator is offered a retry
				 * that can never succeed.
				 */
				path: "catalog/sources",
				element: (
					<RequirePermission permission="admin:catalog:import">
						<CatalogSourcesPage />
					</RequirePermission>
				),
			},
			{
				/*
				 * GST rates (#514). Readable with either permission: finance_ops
				 * holds `admin:catalog:gst` without `admin:catalog:read`. Writing is
				 * gated inside the pages and, regardless, by the server.
				 */
				path: "catalog/gst-rates",
				element: (
					<RequirePermission anyOf={GST_READ_PERMISSIONS}>
						<CatalogGstRatesPage />
					</RequirePermission>
				),
			},
			{
				path: "catalog/gst-rates/:prefix",
				element: (
					<RequirePermission anyOf={GST_READ_PERMISSIONS}>
						<CatalogGstRatePrefixPage />
					</RequirePermission>
				),
			},
			{
				path: "boq-templates",
				element: (
					<RequirePermission permission="admin:boq-templates:read">
						<BoqTemplatesDashboardPage />
					</RequirePermission>
				),
			},
			{
				path: "boq-templates/:templateId",
				element: (
					<RequirePermission permission="admin:boq-templates:read">
						<BoqTemplateEditorPage />
					</RequirePermission>
				),
			},
			{
				path: "payments",
				element: <PaymentsDashboardPage />,
			},
			{
				path: "users",
				element: <UsersDashboardPage />,
			},
			{
				/*
				 * Gated like the catalogue screens: ungated the page renders and
				 * every request 403s behind a generic failure, which is
				 * indistinguishable from the server being down.
				 */
				path: "logs",
				element: (
					<RequirePermission permission="admin:logs:read">
						<LogsDashboardPage />
					</RequirePermission>
				),
			},
			{
				path: "roles",
				element: (
					<RequirePermission permission="admin:roles:read">
						<RolesDashboardPage />
					</RequirePermission>
				),
			},
			{
				// Gated on the write permission, not a read one: the whole screen is
				// the verified toggle, so a read-only operator would see a control
				// that always 403s.
				path: "vendors",
				element: (
					<RequirePermission permission="admin:vendors:verify">
						<VendorsDashboardPage />
					</RequirePermission>
				),
			},
		],
	},
	{
		path: "*",
		element: <Navigate to="/payments" replace />,
	},
]);
